import { useState, useEffect } from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

type Option = { label: string; value: string };

// Module-level cache to share data across component instances
let cachedPolicies: Option[] | null = null;
let cachePromise: Promise<Option[]> | null = null;

export const useIamPolicies = () => {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const baseUrl = configApi.getString('backend.baseUrl');
  const [loading, setLoading] = useState(!cachedPolicies);
  const [error, setError] = useState<Error | null>(null);
  const [options, setOptions] = useState<Option[]>(cachedPolicies || []);

  useEffect(() => {
    // Check and use cached data if available
    if (cachedPolicies) {
      setOptions(cachedPolicies);
      setLoading(false);
      return;
    }

    // If there's already a fetch in progress, wait for it
    if (cachePromise) {
      cachePromise
        .then(data => {
          setOptions(data);
          setLoading(false);
          setError(null);
        })
        .catch(err => {
          setError(err);
          setLoading(false);
        });
      return;
    }

    // Start a new fetch
    setLoading(true);
    setError(null);
    cachePromise = (async () => {
      try {
        const res = await fetchApi.fetch(`${baseUrl}/api/iam/policies?ts=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!res.ok) {
          throw new Error('Failed to load IAM policies');
        }
        const data: Array<{ name: string }> = await res.json();
        const policies = data.map(p => ({ label: p.name, value: p.name }));
        cachedPolicies = policies;
        return policies;
      } catch (err) {
        cachedPolicies = null;
        throw err;
      } finally {
        cachePromise = null;
      }
    })();

    cachePromise
      .then(data => {
        setOptions(data);
        setLoading(false);
        setError(null);
      })
      .catch(err => {
        const newError = err instanceof Error ? err : new Error(String(err));
        setError(newError);
        setLoading(false);
      });
  }, [baseUrl, fetchApi]);

  return { options, loading, error };
};
